import { useRef } from 'react'
import { HTTP_CAMPAIGN_TOKEN_HEADER } from '@captaincause/lib/api/headers'
import { API_URL } from '@captaincause/lib/api/url'
import { useFetch } from '@captaincause/lib/api/useFetch'
import { useNextLocale } from '@captaincause/lib/useNextLocale'
import { useViewportWidth } from '@captaincause/lib/useViewportWidth'
import { Button } from '@captaincause/ui/Button'
import { Dialog } from '@captaincause/ui/Modals/Dialog'
import { Panel } from '@captaincause/ui/Modals/Panel'
import { ScrollTop } from '@captaincause/ui/ScrollTop'
import { tm } from '@captaincause/ui/tm'
import { DesktopView } from './Desktop'
import { MobileView } from './Mobile'
import type {
  Campaign,
  Project,
  ProjectId,
  Project as ProjectType,
} from '@captaincause/types'
import type { ButtonProps } from '@captaincause/ui/Button'
import type { RefObject } from 'react'

export type PanelProps = {
  projectId: ProjectId | undefined
  token: string
  campaign: Campaign
  seeMore?: (props: ProjectId | undefined) => void
  switchProject?: (direction: 'left' | 'right') => void
  close: () => void
}

export type DonateMobileProps = {
  isLoading: boolean
  project: Project | undefined
  campaign: Campaign
  token: string
}

export type DonateDesktopProps = DonateMobileProps & {
  seeMore?: (props: ProjectId | undefined) => void
  switchProject: (direction: 'left' | 'right') => void
  close: () => void
}

const MD_BREAKPOINT = 768

const CloseButton = ({ children, ...props }: ButtonProps) => (
  <Button
    className="h-8 w-8 rounded-full border border-inside bg-light-gray p-0"
    iconClassName="static fill-black"
    {...props}
  />
)

const BackToTop = ({
  container,
  className = '',
}: {
  container: RefObject<HTMLElement>
  className?: string
}) => (
  <ScrollTop
    container={container.current || undefined}
    threshold={600}
    className={tm(
      'sticky bottom-14 left-1/2 h-10 w-10 -translate-x-[50%]',
      className,
    )}
  />
)

const noop = () => {}
export const Panels = ({
  projectId,
  token,
  seeMore = noop,
  switchProject = noop,
  close,
  campaign,
}: PanelProps) => {
  const locale = useNextLocale()

  const {
    data: project,
    isLoading: loading,
    error,
  } = useFetch<ProjectType>({
    url: `${API_URL}/give/projects/${projectId}?locale=${locale}`,
    skipFetch: !projectId,
    headers: [[HTTP_CAMPAIGN_TOKEN_HEADER, token ?? '']],
  })

  const viewportWidth = useViewportWidth()
  const isMobile = Boolean(viewportWidth && viewportWidth <= MD_BREAKPOINT)
  const scrollContainer = useRef<HTMLDivElement>(null)
  const scrollContainerMobile = useRef<HTMLDivElement>(null)
  const isLoading = !project && (loading || !!error)

  return (
    <>
      {/* MOBILE VERSION */}
      <Panel
        ref={scrollContainerMobile}
        orientation="bottom"
        close={close}
        panelClassName="p-0"
        className="md:hidden"
        isOpen={isMobile && !!projectId}
        locale={locale}
        headerCloseIcon="cross"
        headerStyle={{
          color: campaign.button_text_color,
          backgroundColor: 'transparent',
          border: 'none',
          position: 'absolute',
          display: 'flex',
          justifyContent: 'right',
          top: '8px',
          left: '0px',
        }}
        HeaderCloseButton={CloseButton}
        rootNode="__next"
      >
        <MobileView
          isLoading={isLoading}
          project={project}
          campaign={campaign}
          token={token}
        />
        <BackToTop
          container={scrollContainerMobile}
          className="sticky bottom-4 left-1/2 h-10 w-10 -translate-x-[50%]"
        />
      </Panel>

      {/* DESKTOP VERSION */}
      <Dialog
        isOpen={!isMobile && !!projectId}
        close={close}
        className="hidden md:block"
        panelClassName="bg-transparent shadow-none max-w-6xl"
        rootNode="__next"
      >
        <DesktopView
          campaign={campaign}
          close={close}
          isLoading={isLoading}
          project={project}
          seeMore={seeMore}
          switchProject={switchProject}
          token={token}
        />
      </Dialog>
    </>
  )
}
